import {
  CheckCircleTwoTone,
  DownloadOutlined,
  FileOutlined,
  FolderOpenOutlined,
  FolderOutlined,
  LinkOutlined,
  ReloadOutlined,
  SearchOutlined,
  WarningTwoTone
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Card, Empty, Input, Pagination, Select, Space, Tag, Tree, Typography } from 'antd'
import { format, fromUnixTime } from 'date-fns'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import $api from '../../API'
import { useAppDispatch } from '../../store'
import * as previewActions from '../../store/previewer'
import './styles.css'

import type {
  ItemStatus,
  SftpScope,
  SftpTreeNode,
  SftpTreeResponse,
  SourceInfo,
  TreeDataNode
} from './types'
import type { DataNode } from 'antd/es/tree'

const { Text } = Typography

function scopeToKey(sourceKey: string, dirIdx: number): string {
  return `${sourceKey}:${dirIdx}`
}

function parseScopeKey(scopeKey: string): SftpScope | null {
  const [sourceKey, dirIdxRaw] = scopeKey.split(':')
  const dirIdx = Number(dirIdxRaw)

  if (!sourceKey || Number.isNaN(dirIdx)) {
    return null
  }

  return {
    sourceKey,
    dirIdx
  }
}

function formatDate(unixTime?: number): string {
  if (!unixTime) {
    return 'n/a'
  }

  return format(fromUnixTime(unixTime), 'MMM d, yyyy HH:mm:ss')
}

function formatSize(bytes: number): string {
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB']
  let index = 0
  let value = bytes

  while (value >= 1024 && index < units.length - 1) {
    value /= 1024
    index += 1
  }

  return `${value.toFixed(index === 0 ? 0 : 1)} ${units[index]}`
}

function fileNameFromPath(filePath: string): string {
  const parts = filePath.split('/')
  return parts[parts.length - 1] || filePath
}

function renderStatus(status: ItemStatus): JSX.Element {
  if (status === 'success') {
    return <CheckCircleTwoTone twoToneColor='#16a34a' />
  }

  if (status === 'failure') {
    return <WarningTwoTone twoToneColor='#dc2626' />
  }

  return <Text type='secondary'>●</Text>
}

function detectMimeType(fileName: string): string {
  const normalized = fileName.toLowerCase()

  if (normalized.endsWith('.pdf')) {
    return 'application/pdf'
  }

  if (normalized.endsWith('.csv')) {
    return 'text/csv'
  }

  if (normalized.endsWith('.txt')) {
    return 'text/plain'
  }

  if (normalized.endsWith('.xml')) {
    return 'text/xml'
  }

  if (normalized.endsWith('.tif') || normalized.endsWith('.tiff')) {
    return 'image/tiff'
  }

  if (normalized.endsWith('.json')) {
    return 'application/json'
  }

  return 'application/octet-stream'
}

function replaceNodeChildren(
  nodes: TreeDataNode[],
  targetPath: string,
  children: TreeDataNode[]
): TreeDataNode[] {
  return nodes.map(node => {
    if (node.nodePath === targetPath) {
      return {
        ...node,
        children,
        isLeaf: false,
        icon: <FolderOpenOutlined />
      }
    }

    if (!node.children) {
      return node
    }

    return {
      ...node,
      children: replaceNodeChildren(node.children as TreeDataNode[], targetPath, children)
    }
  })
}

function getNodeKind(nodes: TreeDataNode[], targetPath: string): TreeDataNode['kind'] | null {
  for (const node of nodes) {
    if (node.nodePath === targetPath) {
      return node.kind
    }

    if (node.children) {
      const kind = getNodeKind(node.children as TreeDataNode[], targetPath)
      if (kind) {
        return kind
      }
    }
  }

  return null
}

function findNodeByPath(nodes: TreeDataNode[], targetPath: string): TreeDataNode | null {
  for (const node of nodes) {
    if (node.nodePath === targetPath) {
      return node
    }

    if (node.children) {
      const found = findNodeByPath(node.children as TreeDataNode[], targetPath)
      if (found) {
        return found
      }
    }
  }

  return null
}

export function SFTPTreeView(): JSX.Element {
  const dispatch = useAppDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const [treeData, setTreeData] = useState<TreeDataNode[]>([])
  const [loadingPaths, setLoadingPaths] = useState<Record<string, boolean>>({})
  const [selectedPath, setSelectedPath] = useState<string>('')
  const [metaByPath, setMetaByPath] = useState<
    Record<string, { fetchedAt: number; cached: boolean }>
  >({})
  const [downloadingPath, setDownloadingPath] = useState<string | null>(null)
  const [folderMetaByPath, setFolderMetaByPath] = useState<
    Record<
      string,
      {
        fetchedAt: number
        cached: boolean
        total: number
        page: number
        pageSize: number
      }
    >
  >({})

  const queryClient = useQueryClient()

  const scopeParam = searchParams.get('scope')
  const searchTerm = searchParams.get('q') ?? ''
  const expandedKeys = searchParams.getAll('open')
  const page = Math.max(parseInt(searchParams.get('page') ?? '1', 10) || 1, 1)
  const pageSize = Math.max(parseInt(searchParams.get('pageSize') ?? '50', 10) || 50, 1)

  const directoriesQuery = useQuery({
    queryKey: ['diagnostic-sftp-directories'],
    queryFn: async () => {
      const response = await $api.get<SourceInfo[]>('/diagnostic/sftp/tree/list')
      return response.data
    }
  })

  const refreshStatusesMutation = useMutation({
    mutationFn: async () => {
      await $api.post('/diagnostic/sftp/tree/status/refresh')
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['diagnostic-sftp-directories'] })
    }
  })

  const directoryOptions = useMemo(() => {
    return (directoriesQuery.data ?? []).map(directory => ({
      value: scopeToKey(directory.key, directory.dirIdx),
      label: (
        <Space size={8}>
          {renderStatus(directory.status)}
          <span>{directory.name}</span>
          <Text type='secondary'>{directory.directory}</Text>
        </Space>
      )
    }))
  }, [directoriesQuery.data])

  const selectedScopeKey = useMemo(() => {
    const knownScopeKeys = new Set(directoryOptions.map(option => option.value))

    if (scopeParam && knownScopeKeys.has(scopeParam)) {
      return scopeParam
    }

    return directoryOptions[0]?.value ?? null
  }, [directoryOptions, scopeParam])

  const selectedScope = useMemo(() => {
    if (!selectedScopeKey) {
      return null
    }

    return parseScopeKey(selectedScopeKey)
  }, [selectedScopeKey])

  const selectedDirectory = useMemo(() => {
    if (!selectedScopeKey || !directoriesQuery.data) {
      return null
    }

    return (
      directoriesQuery.data.find(
        directory => scopeToKey(directory.key, directory.dirIdx) === selectedScopeKey
      ) ?? null
    )
  }, [directoriesQuery.data, selectedScopeKey])

  const updateParams = useCallback(
    (update: (params: URLSearchParams) => void) => {
      const params = new URLSearchParams(searchParams)
      update(params)
      setSearchParams(params, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  const fetchFolder = useCallback(
    async (directoryPath: string, refresh = false): Promise<SftpTreeResponse> => {
      if (!selectedScope) {
        throw new Error('No scope selected')
      }

      const params = new URLSearchParams({
        name: selectedScope.sourceKey,
        dir: String(selectedScope.dirIdx),
        path: directoryPath,
        page: String(page),
        pageSize: String(pageSize)
      })

      if (searchTerm.trim().length > 0) {
        params.set('query', searchTerm.trim())
      }

      if (refresh) {
        params.set('refresh', '1')
      }

      const response = await $api.get<SftpTreeResponse>(
        `/diagnostic/sftp/tree?${params.toString()}`
      )
      return response.data
    },
    [page, pageSize, searchTerm, selectedScope]
  )

  const downloadFile = useCallback(
    async (filePath: string, fileName: string) => {
      if (!selectedScope) {
        return
      }

      setDownloadingPath(filePath)

      try {
        const params = new URLSearchParams({
          name: selectedScope.sourceKey,
          dir: String(selectedScope.dirIdx),
          path: filePath,
          download: '1'
        })

        const response = await $api.get(`/diagnostic/sftp/tree/file?${params.toString()}`, {
          responseType: 'blob'
        })

        const blob = new Blob([response.data], { type: detectMimeType(fileName) })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        link.remove()
        URL.revokeObjectURL(url)
      } finally {
        setDownloadingPath(null)
      }
    },
    [selectedScope]
  )

  const openFilePreview = useCallback(
    (filePath: string, refresh = false, refreshUrl?: string, explicitName?: string) => {
      if (!selectedScope) {
        return
      }

      let href = ''

      if (refresh && refreshUrl) {
        href = refreshUrl
      } else {
        const params = new URLSearchParams({
          name: selectedScope.sourceKey,
          dir: String(selectedScope.dirIdx),
          path: filePath
        })

        if (refresh) {
          params.set('refresh', '1')
        }

        href = `/diagnostic/sftp/tree/file?${params.toString()}`
      }

      dispatch(
        previewActions.open({
          href,
          mimeType: detectMimeType(explicitName ?? fileNameFromPath(filePath)),
          name: explicitName ?? fileNameFromPath(filePath)
        })
      )
    },
    [dispatch, selectedScope]
  )

  const mapEntryToTreeNode = useCallback(
    (entry: SftpTreeNode): TreeDataNode => {
      const icon =
        entry.type === 'directory' ? (
          <FolderOutlined />
        ) : entry.type === 'link' ? (
          <LinkOutlined />
        ) : (
          <FileOutlined />
        )

      return {
        key: entry.path,
        titleText: entry.name,
        nodePath: entry.path,
        kind: entry.type,
        isLeaf: entry.type !== 'directory',
        className: 'flex-row flex',
        icon,
        title: (
          <div className='flex w-full min-w-0 items-center justify-between gap-3'>
            <div className='flex min-w-0 items-center gap-2 overflow-hidden'>
              <span className='truncate whitespace-nowrap'>{entry.name}</span>
              {entry.type !== 'directory' && <Text type='secondary'>{formatSize(entry.size)}</Text>}
            </div>
            <Space size={4}>
              {entry.type === 'directory' && (
                <Button
                  type='link'
                  size='small'
                  className='shrink-0'
                  icon={<ReloadOutlined />}
                  onClick={event => {
                    event.preventDefault()
                    event.stopPropagation()

                    void (async () => {
                      const response = await fetchFolder(entry.path, true)
                      const children = response.entries.map(mapEntryToTreeNode)
                      setTreeData(prev => replaceNodeChildren(prev, entry.path, children))
                      setMetaByPath(prev => ({
                        ...prev,
                        [entry.path]: {
                          fetchedAt: response.fetchedAt,
                          cached: response.cached
                        }
                      }))
                      setFolderMetaByPath(prev => ({
                        ...prev,
                        [entry.path]: {
                          fetchedAt: response.fetchedAt,
                          cached: response.cached,
                          total: response.total,
                          page: response.page,
                          pageSize: response.pageSize
                        }
                      }))
                    })()
                  }}
                >
                  Refresh
                </Button>
              )}

              {entry.type !== 'directory' && (
                <Button
                  type='link'
                  size='small'
                  className='shrink-0'
                  icon={<DownloadOutlined />}
                  loading={downloadingPath === entry.path}
                  onClick={event => {
                    event.preventDefault()
                    event.stopPropagation()
                    void downloadFile(entry.path, entry.name)
                  }}
                >
                  Download
                </Button>
              )}
            </Space>
          </div>
        )
      }
    },
    [downloadFile, downloadingPath, fetchFolder]
  )

  const loadPath = useCallback(
    async (directoryPath: string, refresh = false) => {
      if (!selectedScope) {
        return
      }

      setLoadingPaths(prev => ({
        ...prev,
        [directoryPath]: true
      }))

      try {
        const response = await fetchFolder(directoryPath, refresh)
        const children = response.entries.map(mapEntryToTreeNode)

        setTreeData(prev => replaceNodeChildren(prev, directoryPath, children))
        setMetaByPath(prev => ({
          ...prev,
          [directoryPath]: {
            fetchedAt: response.fetchedAt,
            cached: response.cached
          }
        }))
        setFolderMetaByPath(prev => ({
          ...prev,
          [directoryPath]: {
            fetchedAt: response.fetchedAt,
            cached: response.cached,
            total: response.total,
            page: response.page,
            pageSize: response.pageSize
          }
        }))
      } finally {
        setLoadingPaths(prev => ({
          ...prev,
          [directoryPath]: false
        }))
      }
    },
    [fetchFolder, mapEntryToTreeNode, selectedScope]
  )

  const refreshSelectedMutation = useMutation({
    mutationFn: async () => {
      const targetPath = selectedPath || selectedDirectory?.directory || '/'
      await loadPath(targetPath, true)
    }
  })

  useEffect(() => {
    if (!selectedScopeKey) {
      return
    }

    if (scopeParam === selectedScopeKey) {
      return
    }

    updateParams(params => {
      params.set('scope', selectedScopeKey)
      params.delete('open')
    })
  }, [scopeParam, selectedScopeKey, updateParams])

  useEffect(() => {
    if (!selectedDirectory) {
      return
    }

    const rootPath = selectedDirectory.directory
    const root: TreeDataNode = {
      key: rootPath,
      titleText: rootPath,
      nodePath: rootPath,
      kind: 'root',
      icon: <FolderOpenOutlined />,
      isLeaf: false,
      title: (
        <Space size={8}>
          <strong>{selectedDirectory.name}</strong>
          <Tag color='blue'>{rootPath} </Tag>
        </Space>
      ),
      children: []
    }

    setTreeData([root])
    setSelectedPath(rootPath)
    setMetaByPath({})
    setFolderMetaByPath({})

    const loadTree = async () => {
      await loadPath(rootPath)

      const toHydrate = [...expandedKeys]
        .filter(pathValue => pathValue !== rootPath)
        .sort((left, right) => left.split('/').length - right.split('/').length)

      for (const pathValue of toHydrate) {
        await loadPath(pathValue)
      }
    }

    void loadTree()

    if (!expandedKeys.includes(rootPath)) {
      updateParams(params => {
        params.delete('open')
        params.append('open', rootPath)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedScopeKey])

  const selectedMeta = selectedPath ? metaByPath[selectedPath] : undefined
  const selectedFolderMeta = selectedPath ? folderMetaByPath[selectedPath] : undefined
  const selectedKind = selectedPath ? getNodeKind(treeData, selectedPath) : null
  const selectedIsDirectory = selectedKind === 'directory' || selectedKind === 'root'

  useEffect(() => {
    if (!selectedPath || !selectedIsDirectory) {
      return
    }

    void loadPath(selectedPath)
  }, [loadPath, page, pageSize, searchTerm, selectedIsDirectory, selectedPath])

  const handleTreeSelect = useCallback(
    (keys: React.Key[]) => {
      const first = keys[0]
      if (typeof first !== 'string') {
        return
      }

      setSelectedPath(first)

      const kind = getNodeKind(treeData, first)
      if (kind === 'file' || kind === 'link' || kind === 'unknown') {
        const node = findNodeByPath(treeData, first)
        openFilePreview(first, false, undefined, node?.titleText)
      }
    },
    [openFilePreview, treeData]
  )

  if (directoriesQuery.isLoading) {
    return <Card loading className='rounded-xl' />
  }

  if (!directoriesQuery.data || directoriesQuery.data.length === 0) {
    return <Empty description='No SFTP directories configured in diagnostic constants.' />
  }

  return (
    <div className='flex flex-col gap-5'>
      <Card className='border-0 shadow-md'>
        <div className='flex flex-wrap items-center justify-between gap-3'>
          <div>
            <h1 className='m-0 text-3xl font-semibold tracking-tight text-slate-900'>
              SFTP Tree View
            </h1>
            <Text type='secondary'>
              Lazy folder explorer with Redis cache (TTL: 7 days). Expand folders to load each next
              layer.
            </Text>
          </div>

          <Button
            icon={<ReloadOutlined />}
            onClick={() => refreshStatusesMutation.mutate()}
            loading={refreshStatusesMutation.isPending}
          >
            Refresh Connections
          </Button>
        </div>
      </Card>

      <Card className='rounded-xl shadow-sm'>
        <div className='grid grid-cols-1 gap-3 md:grid-cols-12'>
          <div className='md:col-span-5'>
            <Text type='secondary'>Source</Text>
            <Select
              className='mt-1 w-full'
              value={selectedScopeKey ?? undefined}
              options={directoryOptions}
              onChange={value => {
                updateParams(params => {
                  params.set('scope', value)
                  params.delete('open')
                  params.delete('q')
                  params.delete('page')
                })
              }}
            />
          </div>

          <div className='md:col-span-5'>
            <Text type='secondary'>Search (stored in URL)</Text>
            <Input
              className='mt-1'
              value={searchTerm}
              allowClear
              prefix={<SearchOutlined />}
              placeholder='Find by file or folder name'
              onChange={event => {
                const nextValue = event.target.value
                updateParams(params => {
                  if (nextValue.trim().length === 0) {
                    params.delete('q')
                  } else {
                    params.set('q', nextValue)
                  }
                  params.set('page', '1')
                })
              }}
            />
          </div>

          <div className='md:col-span-2 flex items-end'>
            <Button
              className='w-full'
              icon={<ReloadOutlined />}
              onClick={() => refreshSelectedMutation.mutate()}
              loading={refreshSelectedMutation.isPending}
              disabled={!selectedIsDirectory}
            >
              Refresh Folder
            </Button>
          </div>
        </div>
      </Card>

      <Card className='rounded-xl shadow-sm'>
        <div className='mb-3 flex flex-wrap items-center gap-2'>
          <Tag color='geekblue'>Expanded paths in URL</Tag>
          <Tag color='green'>Selected: {selectedPath || 'n/a'}</Tag>
          {selectedMeta && (
            <Tag color={selectedMeta.cached ? 'cyan' : 'orange'}>
              {selectedMeta.cached ? 'From cache' : 'Fresh from SFTP'}
            </Tag>
          )}
          {selectedMeta && (
            <Tag color='default'>Cached at: {formatDate(selectedMeta.fetchedAt)}</Tag>
          )}
          {selectedFolderMeta && <Tag color='default'>Total: {selectedFolderMeta.total}</Tag>}
        </div>

        <Tree
          className='sftp-tree-view-tree'
          blockNode
          showIcon
          treeData={treeData as DataNode[]}
          expandedKeys={expandedKeys}
          selectedKeys={selectedPath ? [selectedPath] : []}
          onSelect={handleTreeSelect}
          onExpand={keys => {
            updateParams(params => {
              params.delete('open')
              for (const key of keys) {
                if (typeof key === 'string') {
                  params.append('open', key)
                }
              }
            })
          }}
          filterTreeNode={node => {
            const titleText = findNodeByPath(treeData, String(node.key))?.titleText ?? ''
            const trimmed = searchTerm.trim().toLowerCase()
            return trimmed.length > 0 && titleText.toLowerCase().includes(trimmed)
          }}
          loadData={async treeNode => {
            const nodePath = String(treeNode.key)
            const kind = getNodeKind(treeData, nodePath)

            if (kind !== 'directory' && kind !== 'root') {
              return
            }

            if (loadingPaths[nodePath]) {
              return
            }

            await loadPath(nodePath)
          }}
        />
        {selectedIsDirectory && selectedFolderMeta && selectedFolderMeta.total > 0 && (
          <div className='mt-4 flex items-center justify-between gap-3'>
            <div className='flex items-center gap-3'>
              <Text type='secondary'>Page size</Text>
              <Select
                value={String(pageSize)}
                style={{ width: 120 }}
                options={[
                  { value: '20', label: '20' },
                  { value: '50', label: '50' },
                  { value: '100', label: '100' },
                  { value: '200', label: '200' }
                ]}
                onChange={value => {
                  updateParams(params => {
                    params.set('pageSize', value)
                    params.set('page', '1')
                  })
                }}
              />
            </div>
            <Pagination
              current={page}
              pageSize={pageSize}
              total={selectedFolderMeta.total}
              showSizeChanger={false}
              onChange={nextPage => {
                updateParams(params => {
                  params.set('page', String(nextPage))
                })
              }}
            />
          </div>
        )}
      </Card>
    </div>
  )
}
